import React, { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import PageHelp from '../../components/PageHelp'
import PrintPreviewModal from '../../components/PrintPreviewModal'
import FieldHelp from '../../components/FieldHelp'

interface Invoice {
  id: string
  invoiceNumber: string
  invoiceDate: string
  dueDate: string
  postingDate?: string
  clientOrg: {
    id: string
    name: string
  }
  status: string
  subtotal: string
  taxAmount: string
  total: string
  paidAmount: string
  outstandingAmount: string
  currency?: string
  notes?: string
  terms?: string
  paymentTerms?: string
  items?: InvoiceItemDetail[]
}

interface InvoiceItemDetail {
  id: string
  description: string
  quantity: string
  unitPrice: string
  taxAmount: string
  total: string
  account?: {
    code: string
    name: string
  }
  taxRate?: {
    name: string
    rate: string
  }
}

interface ClientOrganization {
  id: string
  name: string
  domain?: string
}

interface Account {
  id: string
  code: string
  name: string
  rootType: string
}

interface TaxRate {
  id: string
  name: string
  rate: string
  taxType: string
}

interface InvoiceItem {
  description: string
  quantity: string
  unitPrice: string
  accountId: string
  taxRateId: string
}

const Invoices: React.FC = () => {
  const { user } = useAuthStore()
  const { hasPermission, loading: permissionsLoading } = usePermissions(user)
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showPrintPreview, setShowPrintPreview] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null)
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null)
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false)
  const [clientOrgs, setClientOrgs] = useState<ClientOrganization[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [taxRates, setTaxRates] = useState<TaxRate[]>([])
  const [formData, setFormData] = useState({
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    postingDate: new Date().toISOString().split('T')[0],
    clientOrgId: '',
    currency: 'GBP',
    notes: '',
    terms: '',
    paymentTerms: '',
    items: [
      { description: '', quantity: '1', unitPrice: '', accountId: '', taxRateId: '' }
    ] as InvoiceItem[],
  })

  useEffect(() => {
    fetchInvoices()
  }, [])

  useEffect(() => {
    if (showCreateModal) {
      fetchClientOrgs()
      fetchAccounts()
      fetchTaxRates()
    }
  }, [showCreateModal])

  const fetchInvoices = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get('/api/erp/invoices')
      setInvoices(response.data.data || [])
    } catch (error: any) {
      console.error('Error fetching invoices:', error)
      toast.error('Failed to load invoices')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchClientOrgs = async () => {
    try {
      const response = await axios.get('/api/client-organizations')
      if (response.data.success) {
        setClientOrgs(response.data.data || [])
      }
    } catch (error) {
      console.error('Error fetching client organizations:', error)
    }
  }

  const fetchAccounts = async () => {
    try {
      const response = await axios.get('/api/erp/accounts', { params: { isActive: 'true', rootType: 'INCOME' } })
      setAccounts(response.data.data || [])
    } catch (error) {
      console.error('Error fetching accounts:', error)
    }
  }

  const fetchTaxRates = async () => {
    try {
      const response = await axios.get('/api/erp/tax-rates', { params: { isActive: 'true' } })
      setTaxRates(response.data.data || [])
    } catch (error) {
      console.error('Error fetching tax rates:', error)
    }
  }

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: '1', unitPrice: '', accountId: '', taxRateId: '' }]
    })
  }

  const removeItem = (index: number) => {
    if (formData.items.length > 1) {
      setFormData({
        ...formData,
        items: (formData.items || []).filter((_, i) => i !== index)
      })
    }
  }

  const updateItem = (index: number, field: keyof InvoiceItem, value: string) => {
    const newItems = [...formData.items]
    newItems[index] = { ...newItems[index], [field]: value }
    setFormData({ ...formData, items: newItems })
  }

  const calculateTotals = () => {
    try {
      let subtotal = 0
      let taxAmount = 0

      if (!formData || !formData.items || !Array.isArray(formData.items)) {
        return { subtotal: 0, taxAmount: 0, total: 0 }
      }

      // Ensure taxRates is an array
      const safeTaxRates = Array.isArray(taxRates) ? taxRates : []

      formData.items.forEach((item: any) => {
        if (!item || typeof item !== 'object') return
        
        try {
          const quantityStr = item.quantity !== undefined && item.quantity !== null ? String(item.quantity) : '0'
          const unitPriceStr = item.unitPrice !== undefined && item.unitPrice !== null ? String(item.unitPrice) : '0'
          
          const quantity = parseFloat(quantityStr) || 0
          const unitPrice = parseFloat(unitPriceStr) || 0
          
          if (isNaN(quantity) || isNaN(unitPrice)) {
            return // Skip invalid numbers
          }
          
          const itemTotal = quantity * unitPrice
          subtotal += itemTotal

          if (item.taxRateId && safeTaxRates.length > 0) {
            const taxRate = safeTaxRates.find((tr: any) => tr && tr.id === item.taxRateId)
            if (taxRate && taxRate.rate !== undefined && taxRate.rate !== null) {
              const rate = parseFloat(String(taxRate.rate)) || 0
              if (!isNaN(rate)) {
                taxAmount += itemTotal * (rate / 100)
              }
            }
          }
        } catch (err) {
          console.error('Error processing invoice item:', err, item)
          // Skip this item if there's an error
        }
      })

      return { subtotal, taxAmount, total: subtotal + taxAmount }
    } catch (error) {
      console.error('Error in calculateTotals:', error, { formData, taxRates })
      return { subtotal: 0, taxAmount: 0, total: 0 }
    }
  }

  const handlePost = async (invoiceId: string) => {
    if (!confirm('Are you sure you want to post this invoice? This will create GL entries and cannot be undone.')) {
      return
    }
    try {
      await axios.post(`/api/erp/invoices/${invoiceId}/post`)
      toast.success('Invoice posted successfully')
      fetchInvoices()
      if (selectedInvoice && selectedInvoice.id === invoiceId) {
        const response = await axios.get(`/api/erp/invoices/${invoiceId}`)
        setSelectedInvoice(response.data.data)
      }
    } catch (error: any) {
      console.error('Error posting invoice:', error)
      toast.error(error.response?.data?.error || 'Failed to post invoice')
    }
  }

  const handleDelete = async (invoiceId: string) => {
    if (!confirm('Are you sure you want to delete this invoice? This action cannot be undone.')) {
      return
    }
    try {
      await axios.delete(`/api/erp/invoices/${invoiceId}`)
      toast.success('Invoice deleted successfully')
      fetchInvoices()
      if (selectedInvoice && selectedInvoice.id === invoiceId) {
        setShowViewModal(false)
        setSelectedInvoice(null)
      }
    } catch (error: any) {
      console.error('Error deleting invoice:', error)
      toast.error(error.response?.data?.error || 'Failed to delete invoice')
    }
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingInvoice) return

    if (!formData.clientOrgId) {
      toast.error('Please select a client organization')
      return
    }

    if (!formData.items || formData.items.length === 0 || formData.items.some(item => !item.description || !item.accountId)) {
      toast.error('Please fill in all required item fields')
      return
    }

    try {
      const payload = {
        invoiceDate: formData.invoiceDate,
        dueDate: formData.dueDate,
        postingDate: formData.postingDate,
        clientOrgId: formData.clientOrgId,
        currency: formData.currency,
        notes: formData.notes || undefined,
        terms: formData.terms || undefined,
        paymentTerms: formData.paymentTerms || undefined,
        items: (formData.items || []).map(item => ({
          description: item.description,
          quantity: parseFloat(String(item.quantity || '0')) || 0,
          unitPrice: parseFloat(String(item.unitPrice || '0')) || 0,
          accountId: item.accountId,
          taxRateId: item.taxRateId || undefined,
        })),
      }

      await axios.put(`/api/erp/invoices/${editingInvoice.id}`, payload)
      toast.success('Invoice updated successfully')
      setShowEditModal(false)
      setEditingInvoice(null)
      fetchInvoices()
    } catch (error: any) {
      console.error('Error updating invoice:', error)
      toast.error(error.response?.data?.error || 'Failed to update invoice')
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.clientOrgId) {
      toast.error('Please select a client organization')
      return
    }

    if (!formData.items || formData.items.length === 0 || formData.items.some(item => !item.description || !item.accountId)) {
      toast.error('Please fill in all required item fields')
      return
    }

    try {
      const payload = {
        invoiceDate: formData.invoiceDate,
        dueDate: formData.dueDate,
        postingDate: formData.postingDate,
        clientOrgId: formData.clientOrgId,
        currency: formData.currency,
        notes: formData.notes || undefined,
        terms: formData.terms || undefined,
        paymentTerms: formData.paymentTerms || undefined,
        items: (formData.items || []).map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity) || 0,
          unitPrice: parseFloat(item.unitPrice) || 0,
          accountId: item.accountId,
          taxRateId: item.taxRateId || undefined,
        }))
      }

      await axios.post('/api/erp/invoices', payload)
      toast.success('Invoice created successfully')
      setShowCreateModal(false)
      setFormData({
        invoiceDate: new Date().toISOString().split('T')[0],
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        postingDate: new Date().toISOString().split('T')[0],
        clientOrgId: '',
        currency: 'GBP',
        notes: '',
        terms: '',
        paymentTerms: '',
        items: [{ description: '', quantity: '1', unitPrice: '', accountId: '', taxRateId: '' }]
      })
      fetchInvoices()
    } catch (error: any) {
      console.error('Error creating invoice:', error)
      toast.error(error.response?.data?.error || 'Failed to create invoice')
    }
  }

  // Wait for permissions to load before checking
  if (permissionsLoading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  if (!hasPermission('erp_invoice', 'read')) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You don't have permission to view invoices.</p>
        </div>
      </div>
    )
  }

  // Calculate totals - call directly to avoid useMemo dependency issues
  const totals = (() => {
    try {
      return calculateTotals()
    } catch (error) {
      console.error('Error calculating totals:', error)
      return { subtotal: 0, taxAmount: 0, total: 0 }
    }
  })()

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
            <PageHelp pageIdentifier="invoices" title="Invoices" />
          </div>
          <p className="text-gray-600 mt-1">Sales invoices and accounts receivable</p>
        </div>
        {hasPermission('erp_invoice', 'write') && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Create Invoice</span>
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : invoices.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500">No invoices found. Create your first invoice to get started.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Outstanding</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-50 cursor-pointer" onClick={async () => {
                  setIsLoadingInvoice(true)
                  try {
                    const response = await axios.get(`/api/erp/invoices/${invoice.id}`)
                    setSelectedInvoice(response.data.data)
                    setShowViewModal(true)
                  } catch (error: any) {
                    console.error('Error fetching invoice:', error)
                    toast.error('Failed to load invoice details')
                  } finally {
                    setIsLoadingInvoice(false)
                  }
                }}>
                  <td className="px-4 py-2">{invoice.invoiceNumber}</td>
                  <td className="px-4 py-2">{new Date(invoice.invoiceDate).toLocaleDateString()}</td>
                  <td className="px-4 py-2">{invoice.clientOrg.name}</td>
                  <td className="px-4 py-2 text-right">{parseFloat(invoice.total).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                  <td className="px-4 py-2 text-right">{parseFloat(invoice.outstandingAmount).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      invoice.status === 'PAID' ? 'bg-green-100 text-green-800' :
                      invoice.status === 'POSTED' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {invoice.status}
                    </span>
                  </td>
                  <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex space-x-2">
                      <button
                        onClick={async () => {
                          setIsLoadingInvoice(true)
                          try {
                            const response = await axios.get(`/api/erp/invoices/${invoice.id}`)
                            setSelectedInvoice(response.data.data)
                            setShowViewModal(true)
                          } catch (error: any) {
                            console.error('Error fetching invoice:', error)
                            toast.error('Failed to load invoice details')
                          } finally {
                            setIsLoadingInvoice(false)
                          }
                        }}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        disabled={isLoadingInvoice}
                      >
                        View
                      </button>
                      {invoice.status !== 'DRAFT' && hasPermission('erp_financial_report', 'read') && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedInvoice(invoice)
                            setShowPrintPreview(true)
                          }}
                          className="text-green-600 hover:text-green-800 text-sm font-medium"
                        >
                          Preview
                        </button>
                      )}
                      {invoice.status === 'DRAFT' && hasPermission('erp_invoice', 'post') && (
                        <button
                          onClick={() => handlePost(invoice.id)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Post
                        </button>
                      )}
                      {invoice.status === 'DRAFT' && hasPermission('erp_invoice', 'write') && (
                        <button
                          onClick={async () => {
                            setIsLoadingInvoice(true)
                            try {
                              const response = await axios.get(`/api/erp/invoices/${invoice.id}`)
                              setEditingInvoice(response.data.data)
                              // Populate form with invoice data
                              const inv = response.data.data
                              setFormData({
                                invoiceDate: new Date(inv.invoiceDate).toISOString().split('T')[0],
                                dueDate: new Date(inv.dueDate).toISOString().split('T')[0],
                                postingDate: new Date(inv.postingDate).toISOString().split('T')[0],
                                clientOrgId: inv.clientOrgId,
                                currency: inv.currency || 'GBP',
                                notes: inv.notes || '',
                                terms: inv.terms || '',
                                paymentTerms: inv.paymentTerms || '',
                                items: (inv.items || []).map((item: any) => ({
                                  description: item.description || '',
                                  quantity: item.quantity?.toString() || '1',
                                  unitPrice: item.unitPrice?.toString() || '0',
                                  accountId: item.accountId || '',
                                  taxRateId: item.taxRateId || '',
                                })),
                              })
                              fetchClientOrgs()
                              fetchAccounts()
                              fetchTaxRates()
                              setShowEditModal(true)
                            } catch (error: any) {
                              console.error('Error fetching invoice:', error)
                              toast.error('Failed to load invoice for editing')
                            } finally {
                              setIsLoadingInvoice(false)
                            }
                          }}
                          className="text-green-600 hover:text-green-800 text-sm font-medium"
                        >
                          Edit
                        </button>
                      )}
                      {invoice.status === 'DRAFT' && hasPermission('erp_invoice', 'delete') && (
                        <button
                          onClick={() => handleDelete(invoice.id)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Create Invoice</h2>
            <form onSubmit={handleCreate}>
              <div className="space-y-4">
                {/* Basic Info */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Invoice Date *
                      <FieldHelp content="The date the invoice is issued/sent to the customer. This appears on the invoice document and is used for payment terms." />
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.invoiceDate}
                      onChange={(e) => setFormData({ ...formData, invoiceDate: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Due Date *
                      <FieldHelp content="The date when payment is due from the customer. Usually calculated from invoice date based on payment terms (e.g., Net 30)." />
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.dueDate}
                      onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Posting Date *
                      <FieldHelp content="The date used for accounting purposes. This determines which accounting period the transaction belongs to and appears in financial reports. Usually the same as invoice date, but can differ for accrual accounting." />
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.postingDate}
                      onChange={(e) => setFormData({ ...formData, postingDate: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Client Organization *</label>
                    <select
                      required
                      value={formData.clientOrgId}
                      onChange={(e) => setFormData({ ...formData, clientOrgId: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    >
                      <option value="">Select client...</option>
                      {clientOrgs.map(org => (
                        <option key={org.id} value={org.id}>{org.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    >
                      <option value="GBP">GBP</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                </div>

                {/* Invoice Items */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Items *</label>
                  <div className="space-y-3">
                    {(formData.items || []).map((item, index) => (
                      <div key={index} className="grid grid-cols-12 gap-2 p-3 bg-gray-50 rounded border">
                        <div className="col-span-12">
                          <input
                            type="text"
                            required
                            value={item.description}
                            onChange={(e) => updateItem(index, 'description', e.target.value)}
                            placeholder="Item description"
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          />
                        </div>
                        <div className="col-span-3">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            required
                            value={item.quantity}
                            onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                            placeholder="Quantity"
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          />
                        </div>
                        <div className="col-span-3">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            required
                            value={item.unitPrice}
                            onChange={(e) => updateItem(index, 'unitPrice', e.target.value)}
                            placeholder="Unit Price"
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          />
                        </div>
                        <div className="col-span-3">
                          <label className="block text-xs text-gray-600 mb-1">
                            Revenue Account *
                            <FieldHelp content="The income account that categorizes this revenue. Examples: 'Service Revenue', 'Product Sales', 'Consulting Revenue'. This determines which income category appears in your Profit & Loss report. When the invoice is posted, this account is credited (increased) with the item amount." />
                          </label>
                          <select
                            required
                            value={item.accountId}
                            onChange={(e) => updateItem(index, 'accountId', e.target.value)}
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          >
                            <option value="">Select revenue account...</option>
                            {accounts.map(acc => (
                              <option key={acc.id} value={acc.id}>{acc.code} - {acc.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-span-2">
                          <select
                            value={item.taxRateId}
                            onChange={(e) => updateItem(index, 'taxRateId', e.target.value)}
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          >
                            <option value="">No Tax</option>
                            {taxRates.map(tr => (
                              <option key={tr.id} value={tr.id}>{tr.name} ({tr.rate}%)</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-span-1 flex items-center justify-center">
                          {formData.items.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="text-red-600 hover:text-red-800 text-xl font-bold"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addItem}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      + Add Item
                    </button>
                  </div>
                </div>

                {/* Totals */}
                <div className="bg-gray-50 p-4 rounded border">
                  <div className="flex justify-end space-x-6">
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Subtotal:</p>
                      <p className="text-sm text-gray-600">Tax:</p>
                      <p className="text-lg font-bold text-gray-900">Total:</p>
                    </div>
                    <div className="text-right min-w-[120px]">
                      <p className="text-sm text-gray-900">{(totals?.subtotal || 0).toLocaleString('en-GB', { style: 'currency', currency: formData.currency || 'GBP' })}</p>
                      <p className="text-sm text-gray-900">{(totals?.taxAmount || 0).toLocaleString('en-GB', { style: 'currency', currency: formData.currency || 'GBP' })}</p>
                      <p className="text-lg font-bold text-gray-900">{(totals?.total || 0).toLocaleString('en-GB', { style: 'currency', currency: formData.currency || 'GBP' })}</p>
                    </div>
                  </div>
                </div>

                {/* Optional Fields */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={formData.notes || ''}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    placeholder="Additional notes..."
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Create Invoice
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Invoice Modal */}
      {showEditModal && editingInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Edit Invoice {editingInvoice.invoiceNumber}</h2>
            <form onSubmit={handleUpdate}>
              {/* Same form structure as create modal */}
              <div className="space-y-4">
                {/* Basic Info */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Invoice Date *
                      <FieldHelp content="The date when you're sending the invoice. This appears on the invoice document." />
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.invoiceDate}
                      onChange={(e) => setFormData({ ...formData, invoiceDate: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Due Date *
                      <FieldHelp content="The date when payment is due. This determines when the invoice becomes overdue." />
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.dueDate}
                      onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Posting Date *
                      <FieldHelp content="The date used for accounting purposes. This determines which accounting period the invoice belongs to and appears in financial reports. Usually the same as invoice date, but can differ for accrual accounting or period adjustments." />
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.postingDate}
                      onChange={(e) => setFormData({ ...formData, postingDate: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
                  <select
                    required
                    value={formData.clientOrgId}
                    onChange={(e) => setFormData({ ...formData, clientOrgId: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="">Select client...</option>
                    {clientOrgs.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="GBP">GBP</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>

                {/* Items - Same as create modal */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Items *</label>
                  <div className="space-y-2">
                    {(formData.items || []).map((item, index) => (
                      <div key={index} className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-4">
                          <input
                            type="text"
                            required
                            value={item.description}
                            onChange={(e) => updateItem(index, 'description', e.target.value)}
                            placeholder="Description"
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          />
                        </div>
                        <div className="col-span-1">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            required
                            value={item.quantity}
                            onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                            placeholder="Qty"
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          />
                        </div>
                        <div className="col-span-2">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            required
                            value={item.unitPrice}
                            onChange={(e) => updateItem(index, 'unitPrice', e.target.value)}
                            placeholder="Unit Price"
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          />
                        </div>
                        <div className="col-span-3">
                          <label className="block text-xs text-gray-600 mb-1">
                            Revenue Account *
                            <FieldHelp content="The income account that categorizes this revenue. Examples: 'Service Revenue', 'Product Sales', 'Consulting Revenue'. This determines which income category appears in your Profit & Loss report. When the invoice is posted, this account is credited (increased) with the item amount." />
                          </label>
                          <select
                            required
                            value={item.accountId}
                            onChange={(e) => updateItem(index, 'accountId', e.target.value)}
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          >
                            <option value="">Select revenue account...</option>
                            {accounts.map(acc => (
                              <option key={acc.id} value={acc.id}>{acc.code} - {acc.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-span-2">
                          <select
                            value={item.taxRateId}
                            onChange={(e) => updateItem(index, 'taxRateId', e.target.value)}
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          >
                            <option value="">No Tax</option>
                            {taxRates.map(tr => (
                              <option key={tr.id} value={tr.id}>{tr.name} ({tr.rate}%)</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-span-1 flex items-center justify-center">
                          {formData.items.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="text-red-600 hover:text-red-800 text-xl font-bold"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addItem}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      + Add Item
                    </button>
                  </div>
                </div>

                {/* Totals */}
                <div className="bg-gray-50 p-4 rounded border">
                  <div className="flex justify-end space-x-6">
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Subtotal:</p>
                      <p className="text-sm text-gray-600">Tax:</p>
                      <p className="text-lg font-bold text-gray-900">Total:</p>
                    </div>
                    <div className="text-right min-w-[120px]">
                      <p className="text-sm text-gray-900">{(totals?.subtotal || 0).toLocaleString('en-GB', { style: 'currency', currency: formData.currency || 'GBP' })}</p>
                      <p className="text-sm text-gray-900">{(totals?.taxAmount || 0).toLocaleString('en-GB', { style: 'currency', currency: formData.currency || 'GBP' })}</p>
                      <p className="text-lg font-bold text-gray-900">{(totals?.total || 0).toLocaleString('en-GB', { style: 'currency', currency: formData.currency || 'GBP' })}</p>
                    </div>
                  </div>
                </div>

                {/* Optional Fields */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={formData.notes || ''}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    placeholder="Additional notes..."
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false)
                    setEditingInvoice(null)
                  }}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Update Invoice
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Invoice Modal */}
      {showViewModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Invoice {selectedInvoice.invoiceNumber}</h2>
              <button
                onClick={() => {
                  setShowViewModal(false)
                  setSelectedInvoice(null)
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Invoice Details */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Invoice Date</label>
                  <p className="mt-1 text-sm text-gray-900">{new Date(selectedInvoice.invoiceDate).toLocaleDateString()}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Due Date</label>
                  <p className="mt-1 text-sm text-gray-900">{new Date(selectedInvoice.dueDate).toLocaleDateString()}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <p className="mt-1">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      selectedInvoice.status === 'PAID' ? 'bg-green-100 text-green-800' :
                      selectedInvoice.status === 'POSTED' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedInvoice.status}
                    </span>
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Client</label>
                <p className="mt-1 text-sm text-gray-900">{selectedInvoice.clientOrg?.name || '-'}</p>
              </div>

              {/* Invoice Items */}
              {selectedInvoice.items && selectedInvoice.items.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Items</label>
                  <div className="bg-white border rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Tax</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedInvoice.items && selectedInvoice.items.length > 0 && selectedInvoice.items.map((item: any) => (
                          <tr key={item.id}>
                            <td className="px-4 py-2">{item.description || '-'}</td>
                            <td className="px-4 py-2 text-right">{parseFloat(item.quantity?.toString() || '0').toLocaleString()}</td>
                            <td className="px-4 py-2 text-right">{parseFloat(item.unitPrice?.toString() || '0').toLocaleString('en-GB', { style: 'currency', currency: selectedInvoice.currency || 'GBP' })}</td>
                            <td className="px-4 py-2 text-right">
                              {item.taxRate ? `${item.taxRate.name} (${item.taxRate.rate}%)` : 'No Tax'}
                            </td>
                            <td className="px-4 py-2 text-right font-medium">{parseFloat(item.total?.toString() || '0').toLocaleString('en-GB', { style: 'currency', currency: selectedInvoice.currency || 'GBP' })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Totals */}
              <div className="bg-gray-50 p-4 rounded border">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Subtotal</label>
                    <p className="mt-1 text-lg font-semibold text-gray-900">
                      {parseFloat(selectedInvoice.subtotal?.toString() || '0').toLocaleString('en-GB', { style: 'currency', currency: selectedInvoice.currency || 'GBP' })}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Tax Amount</label>
                    <p className="mt-1 text-lg font-semibold text-gray-900">
                      {parseFloat(selectedInvoice.taxAmount?.toString() || '0').toLocaleString('en-GB', { style: 'currency', currency: selectedInvoice.currency || 'GBP' })}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Total</label>
                    <p className="mt-1 text-2xl font-bold text-gray-900">
                      {parseFloat(selectedInvoice.total?.toString() || '0').toLocaleString('en-GB', { style: 'currency', currency: selectedInvoice.currency || 'GBP' })}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Outstanding</label>
                    <p className="mt-1 text-lg font-semibold text-gray-900">
                      {parseFloat(selectedInvoice.outstandingAmount?.toString() || '0').toLocaleString('en-GB', { style: 'currency', currency: selectedInvoice.currency || 'GBP' })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {selectedInvoice.notes && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{selectedInvoice.notes}</p>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-between">
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const response = await axios.get(`/api/erp/invoices/${selectedInvoice.id}/pdf`, {
                        responseType: 'blob',
                      })
                      const url = window.URL.createObjectURL(new Blob([response.data]))
                      const link = document.createElement('a')
                      link.href = url
                      link.setAttribute('download', `invoice-${selectedInvoice.invoiceNumber}.pdf`)
                      document.body.appendChild(link)
                      link.click()
                      link.remove()
                      toast.success('PDF downloaded successfully')
                    } catch (error: any) {
                      console.error('Error downloading PDF:', error)
                      toast.error('Failed to download PDF')
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Download PDF</span>
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const email = prompt('Enter email address to send invoice:')
                    if (!email) return
                    try {
                      await axios.post(`/api/erp/invoices/${selectedInvoice.id}/send-email`, { email })
                      toast.success('Invoice sent via email')
                    } catch (error: any) {
                      console.error('Error sending email:', error)
                      toast.error(error.response?.data?.error || 'Failed to send email')
                    }
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>Send Email</span>
                </button>
                {(selectedInvoice.status === 'POSTED' || selectedInvoice.status === 'PARTIALLY_PAID') && hasPermission('erp_invoice', 'write') && (
                  <button
                    type="button"
                    onClick={async () => {
                      const reason = prompt('Enter reason for amendment (optional):')
                      try {
                        const response = await axios.post(`/api/erp/invoices/${selectedInvoice.id}/amend`, { reason: reason || undefined })
                        toast.success('Invoice amended successfully')
                        setShowViewModal(false)
                        setSelectedInvoice(null)
                        fetchInvoices()
                      } catch (error: any) {
                        console.error('Error amending invoice:', error)
                        toast.error(error.response?.data?.error || 'Failed to amend invoice')
                      }
                    }}
                    className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span>Amend</span>
                  </button>
                )}
                {hasPermission('erp_invoice', 'write') && (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const response = await axios.post(`/api/erp/invoices/${selectedInvoice.id}/duplicate`)
                        toast.success('Invoice duplicated successfully')
                        setShowViewModal(false)
                        setSelectedInvoice(null)
                        fetchInvoices()
                      } catch (error: any) {
                        console.error('Error duplicating invoice:', error)
                        toast.error(error.response?.data?.error || 'Failed to duplicate invoice')
                      }
                    }}
                    className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span>Duplicate</span>
                  </button>
                )}
                {(selectedInvoice.status === 'POSTED' || selectedInvoice.status === 'PARTIALLY_PAID') && hasPermission('erp_invoice', 'write') && (
                  <button
                    type="button"
                    onClick={async () => {
                      const reason = prompt('Enter reason for cancellation (optional):')
                      try {
                        await axios.post(`/api/erp/invoices/${selectedInvoice.id}/cancel`, { reason: reason || undefined })
                        toast.success('Invoice cancelled successfully')
                        setShowViewModal(false)
                        setSelectedInvoice(null)
                        fetchInvoices()
                      } catch (error: any) {
                        console.error('Error cancelling invoice:', error)
                        toast.error(error.response?.data?.error || 'Failed to cancel invoice')
                      }
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>Cancel</span>
                  </button>
                )}
                {(selectedInvoice.status === 'POSTED' || selectedInvoice.status === 'PARTIALLY_PAID') && hasPermission('erp_payment', 'write') && parseFloat(selectedInvoice.outstandingAmount) > 0 && (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        // Get payment data from invoice
                        const paymentDataResponse = await axios.get(`/api/erp/invoices/${selectedInvoice.id}/make-payment`)
                        const paymentData = paymentDataResponse.data.data

                        // Create payment with pre-filled data
                        await axios.post(`/api/erp/invoices/${selectedInvoice.id}/create-payment`, {
                          paymentDate: new Date().toISOString().split('T')[0],
                          postingDate: new Date().toISOString().split('T')[0],
                          amount: parseFloat(selectedInvoice.outstandingAmount),
                          paymentMethod: 'BANK_TRANSFER',
                          accountId: paymentData.bankAccount?.id || '',
                          reference: `Payment for ${selectedInvoice.invoiceNumber}`,
                          description: `Payment for invoice ${selectedInvoice.invoiceNumber}`,
                        })

                        toast.success('Payment created successfully')
                        setShowViewModal(false)
                        setSelectedInvoice(null)
                        fetchInvoices()
                      } catch (error: any) {
                        console.error('Error creating payment:', error)
                        toast.error(error.response?.data?.error || 'Failed to create payment')
                      }
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span>Make Payment</span>
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowViewModal(false)
                  setSelectedInvoice(null)
                }}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showPrintPreview && selectedInvoice && (
        <PrintPreviewModal
          isOpen={showPrintPreview}
          onClose={() => {
            setShowPrintPreview(false)
            setSelectedInvoice(null)
          }}
          documentType="INVOICE"
          documentId={selectedInvoice.id}
        />
      )}
    </div>
  )
}

export default Invoices
